import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { ValidatorForm } from 'react-form-validator-core';
import { Select, ValidatedInput, Label, FormGroup, Button, Box, Flex, Hr, Text } from 'core/ui/atoms';
import { ReactComponent as BackButtonIcon } from 'core/assets/images/V2/icons/BackButton.svg';
import { ERROR_MESSAGES, US_STATES } from 'core/api/constants';

const NewAddress = ({ goBack, handleNewAddress }) => {
  const [formData, setFormData] = useState({ street1: '', street2: '', city: '', instructions: '', zipcode: '', state: '' });
  const states = US_STATES.map((s) => ({ title: s.name, value: s.abbreviation }));

  // eslint-disable-next-line no-console
  console.log('formData', formData);

  const zipToState = (zip) => {
    if (!zip || zip.length < 5) return '';
    const zipInt = parseInt(zip, 10);
    if (zipInt >= 90000 && zipInt <= 96162) return 'CA';
    if (zipInt >= 10000 && zipInt <= 14975) return 'NY';
    // ...add more ranges as needed...
    return '';
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === 'zipcode') {
        const newState = value.length === 5 ? zipToState(value) : prev.state;
        return { ...prev, [name]: value, state: newState };
      }
      return { ...prev, [name]: value };
    });
  };

  return (
    <Box>
      <ModalHeader>
        <IconButton onClick={() => goBack(false)} aria-label="Back">
          <BackButtonIcon />
        </IconButton>
        <ModalTitle>New Address</ModalTitle>
      </ModalHeader>
      <Hr />
      <Form
        noValidate
        instantValidate={false}
      >
        <FormGroupCustom>
          <InputLabel required htmlFor='street1'>Address</InputLabel>
          <ValidatedInputText
            type="text"
            name="street1"
            id="street1"
            placeholder="ex. 123 W. Main St"
            value={formData.street1 || ''}
            onChange={onChange}
            validators={['required']}
            withRequiredValidator={true}
            errorMessages={[ERROR_MESSAGES.required]}
          />
        </FormGroupCustom>
        <FormGroupCustom>
          <InputLabel htmlFor='street2'>Apartment or Suite</InputLabel>
          <ValidatedInputText
            type="text"
            name="street2"
            id="street2"
            placeholder="ex. Apt 007"
            value={formData.street2 || ''}
            onChange={onChange}
          />
        </FormGroupCustom>
        <FormGroupCustom>
          <InputLabel required htmlFor="city">City</InputLabel>
          <ValidatedInputText
            type="text"
            name="city"
            id="city"
            placeholder="ex. San Francisco"
            value={formData.city || ''}
            onChange={onChange}
            validators={['required']}
            withRequiredValidator={true}
            errorMessages={[ERROR_MESSAGES.required]}
          />
        </FormGroupCustom>
        <Flex gap="12px">
          <FormGroupCustom style={{ flex: 1 }}>
            <FormGroup>
              <Label required htmlFor="states">
                State
              </Label>
              <CustomSelect
                id="states"
                options={states}
                onChange={onChange}
                selected={formData.state}
                readOnly
              />
            </FormGroup>
          </FormGroupCustom>
          <FormGroupCustom style={{ flex: 1 }}>
            <InputLabel required htmlFor="zipcode">Zip</InputLabel>
            <ValidatedInputText
              type="text"
              name="zipcode"
              id="zipcode"
              placeholder="ex. 12345"
              maxLength={5}
              value={formData.zipcode || ''}
              onChange={onChange}
            />
          </FormGroupCustom>
        </Flex>
        <FormGroupCustom>
          <InputLabel required>Overnight Courier Delivery Instructions</InputLabel>
          <InstructionsInput
            as="textarea"
            rows={3}
            name="instructions"
            value={formData.instructions || ''}
            onChange={onChange}
            placeholder="Help couriers find your home in the dark and place your box in the right place. If possible, include a backup option (eg, if the lobby is locked or the gate code doesn't work)."
            validators={['required']}
            errorMessages={[ERROR_MESSAGES.required]}
            style={{ resize: 'vertical' }}
          />
        </FormGroupCustom>
      </Form>
      <StyledPaymentButton onClick={() => handleNewAddress(formData)}>
        SAVE
      </StyledPaymentButton>
    </Box>
  );
};

NewAddress.propTypes = {
  goBack: PropTypes.func.isRequired,
  handleNewAddress: PropTypes.func.isRequired,
};

const StyledPaymentButton = styled(Button)`
  padding: 12px 10px;
  border-radius: 4px;
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background: ${(p) => p.theme.colors.green.light};
  margin-bottom: 20px;
`;

const FormGroupCustom = styled(FormGroup)`
  margin-bottom: 0px;
`;

const CustomSelect = styled(Select)`
  padding: 0;
  .select-selected {
    margin: 0;
    padding: 0.7em 1em;
  }
`;

const InstructionsInput = styled(ValidatedInput)`
  height: 100px;
  width: 100%;
  border-radius: 4px;
  border: solid 1px #36443A;
  ::placeholder {
    color: #C5D1C0;
    opacity: 1;
    font-size: 14px;
    padding: 10px;
  }
`;

const ValidatedInputText = styled(ValidatedInput)`
  border-radius: 4px;
  border: solid 1px #36443A;
  ::placeholder {
    color: #C5D1C0;
    opacity: 1;
    font-size: 14px;
  }
`;

const InputLabel = styled(Label)`
  padding: 0.5em 0 0.5em 0em;
`;

const Form = styled(ValidatorForm)`
  display: block;
  margin: 0vh auto 3vh auto;
  width: 95%;
`;

const ModalHeader = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: 24px 16px 0 16px;
  margin-top: 70px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
  min-width: 32px;
  min-height: 32px;
`;

const ModalTitle = styled(Text)`
  flex: 1;
  text-align: center;
  font-size: 30px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 0.9px;
  margin-top: 0px;
  margin-bottom: 10px;
  font-family: ${({ theme }) => theme.typography.familyLarken};
  color: ${({ theme }) => theme.colors.darkGreen};
`;

export default NewAddress;
